import {
	ASK_AI_AUDIT_ACTIONS,
	useCreateAskAiAuditMutation,
	useCreateAskAiEmailDraftMutation,
	useCreateAskAiSessionMutation,
	useLazyGetAskAiSessionMessagesQuery,
	useSendAskAiMessageMutation,
	useUploadAskAiDocumentMutation,
} from '@/features/askAi/askAiApi';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useCreateNoteMutation } from '@/features/engagements/engagementsApi';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	useGetWorkspacePageQuery,
	useSaveWorkspacePageMutation,
} from '@/features/workspaces/workspacesApi';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import { useObjectContext } from '@/hooks/useObjectContext';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	buildCitationHtml,
	buildTaskPrefillFromMessage,
	buildUnlayerDesignFromHtml,
	escapeHtml,
	markdownToHtml,
	normalizeAskAiMessageAttachment,
	uploadSharepointFilesToSession,
} from './helpers';
import useAskAIContextSources from './useAskAIContextSources';

const AskAIContext = createContext(null);

export const useAskAIContext = () => {
	const context = useContext(AskAIContext);
	if (!context) {
		throw new Error('useAskAIContext must be used within an AskAIContextProvider');
	}
	return context;
};

export const AskAIContextProvider = ({ children }) => {
	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const [session, setSession] = useState(null);
	const [messages, setMessages] = useState([]);
	const [draft, setDraft] = useState('');
	const [localBusy, setLocalBusy] = useState(false);
	const [selectedSources, setSelectedSources] = useState([]);
	const [isSelectFilesOpen, setIsSelectFilesOpen] = useState(false);

	const dispatch = useDispatch();

	const dialogData = useSelector((state) => state.dialog?.data?.askAi) ?? {};

	const { clientRow, dealRow, isClientView } = useObjectContext();

	const resolvedClient = dialogData.client ?? clientRow ?? null;
	const resolvedDeal = dialogData.deal ?? dealRow ?? null;

	const contextType = dialogData.contextType ?? (isClientView ? 'client' : 'deal');
	const isClientContext = contextType === 'client';
	const objectId = isClientContext ? resolvedClient?.clientId : resolvedDeal?.dealId;

	const clientForOwner = resolvedClient
		? {
				clientId: resolvedClient.clientId,
				ownerDocId: resolvedClient.ownerDocId,
				clientRow: resolvedClient,
			}
		: null;

	const { correctOwner: folderOwnerId } = getCorrectFolderOwner({
		deal: isClientContext ? undefined : resolvedDeal,
		client: clientForOwner,
	});

	const { data: folderOwner } = useGetUserDetailsQuery(
		{ userId: folderOwnerId },
		{ skip: !folderOwnerId },
	);

	const sharepointItemId = useMemo(
		() =>
			isClientContext
				? (resolvedClient?.driveId ?? 'root')
				: (resolvedDeal?.driveId ?? 'root'),
		[isClientContext, resolvedClient?.driveId, resolvedDeal?.driveId],
	);

	const sharepointDriveId = useMemo(
		() =>
			(isClientContext
				? resolvedClient?.clientFolderDriveId
				: (resolvedDeal?.clientFolderDriveId ?? resolvedClient?.clientFolderDriveId)) ??
			resolvedClient?.clientFolderDriveId ??
			folderOwner?.clientFolderDriveId ??
			userDetails?.clientFolderDriveId,
		[
			isClientContext,
			resolvedClient?.clientFolderDriveId,
			resolvedDeal?.clientFolderDriveId,
			folderOwner?.clientFolderDriveId,
			userDetails?.clientFolderDriveId,
		],
	);

	const { contextSources, contextLoading } = useAskAIContextSources({
		client: resolvedClient,
		deal: resolvedDeal,
		objectId,
		contextType,
	});

	const [createSession, createSessionState] = useCreateAskAiSessionMutation({
		fixedCacheKey: 'ask-ai',
	});
	const [loadMessages] = useLazyGetAskAiSessionMessagesQuery();
	const [sendMessage, sendMessageState] = useSendAskAiMessageMutation({
		fixedCacheKey: 'ask-ai',
	});
	const [uploadDocument, uploadState] = useUploadAskAiDocumentMutation({
		fixedCacheKey: 'ask-ai',
	});
	const [createEmailDraft] = useCreateAskAiEmailDraftMutation();
	const [createAudit] = useCreateAskAiAuditMutation();
	const [createNote, createNoteState] = useCreateNoteMutation({ fixedCacheKey: 'ask-ai' });
	const [saveWorkspacePage, saveWorkspaceState] = useSaveWorkspacePageMutation({
		fixedCacheKey: 'ask-ai',
	});

	// The workspace belongs to the deal, or to the client in a client context.
	const workspaceDealId = isClientContext ? undefined : resolvedDeal?.id;
	const workspaceClientId = isClientContext ? resolvedClient?.clientId : undefined;

	const { data: workspacePage } = useGetWorkspacePageQuery(
		{ dealId: workspaceDealId, clientId: workspaceClientId },
		{
			skip: !workspaceDealId && !workspaceClientId,
		},
	);

	useEffect(() => {
		if (session || !resolvedClient?.clientId) return;

		const initialise = async () => {
			try {
				const nextSession = await createSession({
					contextType,
					clientId: resolvedClient?.clientId,
					dealId: resolvedDeal?.dealId ?? null,
					clientSnapshot: resolvedClient ?? null,
					dealSnapshot: resolvedDeal ?? null,
				}).unwrap();
				setSession(nextSession?.session ?? nextSession);
				const sessionId = nextSession?.session?.sessionId ?? nextSession?.sessionId;
				if (sessionId) {
					const loadedMessages = await loadMessages(sessionId).unwrap();
					setMessages(
						(loadedMessages ?? []).map((message) => ({ ...message, animate: false })),
					);
				}
			} catch (error) {
				dispatch(
					showToast({
						message: error?.data?.error ?? 'Failed to open Folio AI.',
						error: true,
					}),
				);
			}
		};

		initialise();
	}, [session, resolvedClient?.clientId, resolvedDeal?.dealId, contextType]);

	const isBusy =
		localBusy ||
		contextLoading ||
		createSessionState.isLoading ||
		sendMessageState.isLoading ||
		uploadState.isLoading ||
		createNoteState.isLoading ||
		saveWorkspaceState.isLoading;

	const sessionId = session?.sessionId ?? session?.id ?? null;

	const buildOutputSubject = (message) =>
		(message?.subject ?? '').trim() ||
		(isClientContext
			? `Folio AI - ${resolvedClient?.name ?? 'Client'}`
			: `Folio AI - ${resolvedDeal?.dealName ?? 'Deal'}`);

	const buildOutputHtml = (message, includeCitations = true) =>
		`${markdownToHtml(message?.content ?? '')}${
			includeCitations ? buildCitationHtml(message?.citations ?? []) : ''
		}`;

	const refreshMessages = async () => {
		if (!sessionId) return;
		const loadedMessages = await loadMessages(sessionId).unwrap();
		setMessages(loadedMessages ?? []);
	};

	const handleSend = async (promptOverride = null) => {
		const nextDraft = (promptOverride ?? draft).trim();
		if (!sessionId || !nextDraft) return;

		setLocalBusy(true);
		try {
			const pendingAttachments = selectedSources.map((file) =>
				normalizeAskAiMessageAttachment(file),
			);

			const optimisticUserMessage = {
				id: `local-user-${Date.now()}`,
				role: 'user',
				content: nextDraft,
				createdAt: Date.now(),
				...(pendingAttachments.length > 0 && { attachments: pendingAttachments }),
			};
			setMessages((current) => [...current, optimisticUserMessage]);
			if (!promptOverride) {
				setDraft('');
			}

			let messageAttachments = pendingAttachments;

			if (selectedSources.length > 0) {
				const { failedFiles, uploadedFiles } = await uploadSharepointFilesToSession({
					files: selectedSources,
					sessionId,
					uploadDocument,
					sharepointDriveId,
					sharepointItemId,
				});

				if (uploadedFiles.length > 0) {
					messageAttachments = uploadedFiles;
				} else if (failedFiles.length > 0) {
					const failedKeys = new Set(failedFiles.map((file) => file.id ?? file.name));
					messageAttachments = pendingAttachments.filter(
						(file) => !failedKeys.has(file.id ?? file.name),
					);
				}

				if (failedFiles.length > 0) {
					const failedKeys = new Set(failedFiles.map((file) => file.id ?? file.name));
					setSelectedSources((current) =>
						current.filter((file) => failedKeys.has(file.id ?? file.name)),
					);
					dispatch(
						showToast({
							message: `Could not attach ${failedFiles.length} ${failedFiles.length === 1 ? 'file' : 'files'}.`,
							error: true,
						}),
					);
				} else {
					setSelectedSources([]);
				}

				setMessages((current) =>
					current.map((message) =>
						message.id === optimisticUserMessage.id
							? {
									...message,
									...(messageAttachments.length > 0 && {
										attachments: messageAttachments,
									}),
								}
							: message,
					),
				);
			}

			const assistantMessage = await sendMessage({
				sessionId,
				message: nextDraft,
				preloadedSources: contextSources,
			}).unwrap();

			const userMessage = {
				...optimisticUserMessage,
				...(messageAttachments.length > 0 && { attachments: messageAttachments }),
			};

			setMessages((current) => [
				...current.filter((message) => message.id !== optimisticUserMessage.id),
				userMessage,
				{ ...assistantMessage, animate: true },
			]);
		} catch (error) {
			setMessages((current) =>
				current.filter((message) => !String(message.id ?? '').startsWith('local-user-')),
			);
			dispatch(
				showToast({
					message: error?.data?.error ?? 'Failed to send Folio AI message.',
					error: true,
				}),
			);
		} finally {
			setLocalBusy(false);
		}
	};

	const handleCopy = async (message) => {
		try {
			await navigator.clipboard.writeText(message?.content ?? '');
			dispatch(showToast({ message: 'Copied Folio AI output.', success: true }));
			if (sessionId) {
				createAudit({
					sessionId,
					action: ASK_AI_AUDIT_ACTIONS.output_copied,
					messageId: message?.messageId ?? message?.id ?? null,
				});
			}
		} catch {
			dispatch(showToast({ message: 'Could not copy Folio AI output.', error: true }));
		}
	};

	const handleCreateNote = (message) => {
		const objectId = isClientContext ? resolvedClient?.clientId : resolvedDeal?.dealId;
		if (!objectId) return;

		const noteDialogData = {
			id: objectId,
			html: buildOutputHtml(message, true),
			subject: buildOutputSubject(message),
			client: resolvedClient,
			deal: resolvedDeal,
		};

		dispatch(
			openDialog({
				dialogName: 'note',
				data: {
					...noteDialogData,
				},
			}),
		);

		if (sessionId) {
			createAudit({
				sessionId,
				action: ASK_AI_AUDIT_ACTIONS.note_dialog_opened,
				messageId: message?.messageId ?? message?.id ?? null,
			});
		}
	};

	const handleSaveNote = async (message) => {
		const objectType = isClientContext ? 'client' : (resolvedDeal?.objectType ?? 'deal');
		const hasTarget = isClientContext ? resolvedClient?.clientId : resolvedDeal?.dealId;
		if (!hasTarget) return;

		try {
			await createNote({
				...(resolvedClient?.clientId && { clientId: resolvedClient.clientId }),
				...(!isClientContext && { dealId: resolvedDeal?.dealId }),
				noteBody: buildOutputHtml(message, true),
				objectType,
				subject: buildOutputSubject(message),
			}).unwrap();
			dispatch(showToast({ message: 'Saved Folio AI output as a note.', success: true }));
			if (sessionId) {
				createAudit({
					sessionId,
					action: ASK_AI_AUDIT_ACTIONS.saved_as_note,
					messageId: message?.messageId ?? message?.id ?? null,
				});
			}
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.error ?? 'Could not save Folio AI output as a note.',
					error: true,
				}),
			);
		}
	};

	const handleSaveWorkspace = async (message) => {
		const existingHtml = workspacePage?.html ?? '';
		const blockHtml = `<h2>${escapeHtml(
			buildOutputSubject(message),
		)}</h2>${buildOutputHtml(message, true)}`;
		const nextHtml = `${existingHtml}${existingHtml ? '<hr/>' : ''}${blockHtml}`;

		try {
			await saveWorkspacePage({
				dealId: workspaceDealId,
				clientId: workspaceClientId,
				html: nextHtml,
			}).unwrap();
			dispatch(
				showToast({ message: 'Saved Folio AI output to the workspace.', success: true }),
			);
			if (sessionId) {
				createAudit({
					sessionId,
					action: ASK_AI_AUDIT_ACTIONS.save_workspace,
					messageId: message?.messageId ?? message?.id ?? null,
				});
			}
		} catch (error) {
			dispatch(
				showToast({
					message:
						error?.data?.error ?? 'Could not save Folio AI output to the workspace.',
					error: true,
				}),
			);
		}
	};

	const handleComposeEmail = (message) => {
		const html = buildOutputHtml(message, false);
		// dispatch(closeDialog('askAi'));
		dispatch(
			openDialog({
				dialogName: 'unlayerEmail',
				data: {
					clientLocked: true,
					dealLocked: Boolean(resolvedDeal),
					client: resolvedClient,
					deal: resolvedDeal ?? null,
					subject: buildOutputSubject(message),
					editorHtml: html,
					editorDesign: buildUnlayerDesignFromHtml(html),
				},
			}),
		);
		if (sessionId) {
			createAudit({
				sessionId,
				action: ASK_AI_AUDIT_ACTIONS.email_composer_opened,
				messageId: message?.messageId ?? message?.id ?? null,
			});
		}
	};

	// Runs in the background: the drawer only opens from the toast's "Send email" button
	const handleGenerateEmailDraft = async (message) => {
		const messageId = message?.messageId ?? message?.id;
		const toastId = `ask-ai-email-draft-${messageId}`;
		dispatch(
			showToast({
				id: toastId,
				message: 'Generating email draft from Folio AI...',
				loading: true,
				autohide: false,
			}),
		);
		try {
			const draftId = await createEmailDraft({ sessionId, messageId }).unwrap();
			dispatch(
				showToast({
					id: toastId,
					message: 'Draft successfully created from Folio AI',
					success: true,
					autohide: false,
					action: {
						label: 'Send email',
						dialogName: 'unlayerEmail',
						data: {
							clientLocked: true,
							dealLocked: Boolean(resolvedDeal),
							client: resolvedClient,
							deal: resolvedDeal ?? null,
							draftId,
							draftClientId: resolvedClient?.clientId,
							draftDealId: resolvedDeal?.dealId,
						},
					},
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					id: toastId,
					message: error?.data?.error ?? 'Could not create an email draft from Folio AI.',
					error: true,
				}),
			);
		}
	};

	const handleCreateTask = (message) => {
		const contextLabel = isClientContext
			? (resolvedClient?.name ?? 'this client')
			: (resolvedDeal?.dealName ?? 'this deal');
		const prefill = buildTaskPrefillFromMessage(message, contextLabel);

		// dispatch(closeDialog('askAi'));
		dispatch(
			openDialog({
				dialogName: 'task',
				data: {
					client: resolvedClient,
					deal: resolvedDeal ?? null,
					task: {
						subject: prefill.subject,
						body: prefill.body,
						clientId: resolvedClient?.clientId ?? null,
						dealId: resolvedDeal?.dealId ?? null,
						ownerDocId: userDetails?.userId ?? null,
						priority: prefill.priority,
						taskType: 'TODO',
					},
				},
			}),
		);

		if (sessionId) {
			createAudit({
				sessionId,
				action: ASK_AI_AUDIT_ACTIONS.task_dialog_opened,
				messageId: message?.messageId ?? message?.id ?? null,
			});
		}
	};

	const openSelectFiles = () => setIsSelectFilesOpen(true);
	const closeSelectFiles = () => setIsSelectFilesOpen(false);

	const addSelectedSources = useCallback((files = []) => {
		if (!files.length) return;
		setSelectedSources((prev) => {
			const next = [...prev];
			for (const file of files) {
				const fileId = file?.id ?? file?.name;
				if (!next.some((existing) => (existing?.id ?? existing?.name) === fileId)) {
					next.push(file);
				}
			}
			return next;
		});
	}, []);

	const removeSelectedSource = useCallback((fileToRemove) => {
		setSelectedSources((prev) =>
			prev.filter((file) => {
				if (file === fileToRemove) return false;
				const fileKey = file?.id ?? file?.name;
				const removeKey = fileToRemove?.id ?? fileToRemove?.name;
				return fileKey !== removeKey;
			}),
		);
	}, []);

	const clearSelectedSources = useCallback(() => setSelectedSources([]), []);

	const value = {
		session,
		messages,
		contextType,
		draft,
		setDraft,
		localBusy,
		setLocalBusy,
		selectedSources,
		setSelectedSources,
		addSelectedSources,
		removeSelectedSource,
		clearSelectedSources,
		isSelectFilesOpen,
		openSelectFiles,
		closeSelectFiles,
		sharepointItemId,
		sharepointDriveId,
		isClientContext,
		resolvedClient,
		resolvedDeal,
		contextLoading,
		handleCreateTask,
		handleComposeEmail,
		handleGenerateEmailDraft,
		handleSaveNote,
		handleCreateNote,
		handleSaveWorkspace,
		handleCopy,
		handleSend,
		isBusy,
		refreshMessages,
	};

	return <AskAIContext.Provider value={value}>{children}</AskAIContext.Provider>;
};
